import React, { useState, useEffect, useRef } from 'react';
import { adminAPI, roomsAPI, productsAPI } from './services/api';
import { validatePhone, formatPhoneDisplay } from './utils/phoneUtils';
import './ManualRoomBookingModal.css';

interface Room {
  id: string;
  name: string;       // room_number (e.g. "Bahçe Odası 1")
  typeName: string;   // room_type_name (e.g. "Deniz Manzaralı Oda 3 Kişi")
  max_adults: number;
  max_children: number;
  asistpro_room_id: string;
}

interface ManualRoomBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: any) => void;
}

// Timezone-safe tarih yardımcıları
const formatDate = (d: Date): string => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const addDays = (dateStr: string, days: number): string => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return formatDate(dt);
};

const diffDays = (startStr: string, endStr: string): number => {
  const [y1, m1, d1] = startStr.split('-').map(Number);
  const [y2, m2, d2] = endStr.split('-').map(Number);
  const a = new Date(y1, m1 - 1, d1);
  const b = new Date(y2, m2 - 1, d2);
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
};

const ManualRoomBookingModal: React.FC<ManualRoomBookingModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availableAsistproIds, setAvailableAsistproIds] = useState<Set<string>>(new Set());
  const [availableRoomsData, setAvailableRoomsData] = useState<any[]>([]);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const availTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Backend'den çekilen yemek fiyatları
  const [mealPrices, setMealPrices] = useState({
    dinner: { adult: 0, child_0_6: 0, child_7_12: 0 },
    breakfast: { adult: 0, child_0_6: 0, child_7_12: 0 }
  });
  
  // Form state
  const [formData, setFormData] = useState({
    roomTypeId: '',
    checkIn: '',
    checkOut: '',
    adults: 2,
    children: 0,
    childrenAges: [] as number[],
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    specialRequests: '',
    includeBreakfast: false,
    includeDinner: false
  });

  // Kapalı günler state (akşam yemeği kapalı günler — 0=Pazar, 1=Pazartesi, ...)
  const [dinnerClosedDays, setDinnerClosedDays] = useState<number[]>([1]); // Varsayılan: Pazartesi

  // Fiyat state
  const [pricing, setPricing] = useState({
    roomPrice: 0,
    breakfastPrice: 0,
    dinnerPrice: 0,
    totalPrice: 0,
    nights: 0,
    billableBreakfastNights: 0,
    billableDinnerNights: 0
  });

  const [error, setError] = useState('');

  // Fatura bilgileri
  const [invoiceType, setInvoiceType] = useState<'individual' | 'corporate'>('individual');
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceTckn, setInvoiceTckn] = useState('');
  const [invoiceTaxNumber, setInvoiceTaxNumber] = useState('');
  const [invoiceTaxOffice, setInvoiceTaxOffice] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceDistrict, setInvoiceDistrict] = useState('');
  const [invoiceCity, setInvoiceCity] = useState('');
  const [invoicePostalCode, setInvoicePostalCode] = useState('');

  // Bugünün tarihi (min date için)
  const today = formatDate(new Date());
  const minCheckOut = formData.checkIn ? addDays(formData.checkIn, 1) : addDays(today, 1);

  // Kapasiteye göre filtrelenmiş odalar
  const filteredRooms = allRooms.filter(room => {
    if (formData.children > 0 && room.max_children <= 0) return false;
    if (formData.adults > room.max_adults) return false;
    return true;
  });

  // Müsaitlik kontrolü — tarih/misafir değişince availability API çağır
  useEffect(() => {
    if (!formData.checkIn || !formData.checkOut || formData.checkOut <= formData.checkIn) return;
    if (formData.children > 0 && formData.childrenAges.length !== formData.children) return;

    // Müsaitlik verisi değişeceği için fiyatı sıfırla
    setPricing({ roomPrice: 0, breakfastPrice: 0, dinnerPrice: 0, totalPrice: 0, nights: 0, billableBreakfastNights: 0, billableDinnerNights: 0 });

    if (availTimerRef.current) clearTimeout(availTimerRef.current);
    availTimerRef.current = setTimeout(() => {
      checkAvailability();
    }, 400);

    return () => {
      if (availTimerRef.current) clearTimeout(availTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.checkIn, formData.checkOut, formData.adults, formData.children, formData.childrenAges]);

  const checkAvailability = async () => {
    try {
      setLoadingAvailability(true);
      setAvailabilityError('');

      const agesStr = formData.children > 0 ? formData.childrenAges.join(',') : '';
      const response = await roomsAPI.getAvailability({
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        adults: formData.adults,
        children: formData.children,
        childrenAges: agesStr
      });

      const rooms = response.data.data || [];
      const ids = new Set<string>(rooms.map((r: any) => r.roomTypeId));
      setAvailableAsistproIds(ids);
      setAvailableRoomsData(rooms);
      setAvailabilityChecked(true);

      // Seçili oda artık müsait değilse temizle
      if (formData.roomTypeId) {
        const selectedRoom = allRooms.find(r => r.id === formData.roomTypeId);
        if (selectedRoom) {
          const asistproId = (selectedRoom as any).asistpro_room_id;
          if (asistproId && !ids.has(asistproId)) {
            setFormData(prev => ({ ...prev, roomTypeId: '' }));
            setPricing({ roomPrice: 0, breakfastPrice: 0, dinnerPrice: 0, totalPrice: 0, nights: 0, billableBreakfastNights: 0, billableDinnerNights: 0 });
          }
        }
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || '';
      if (msg.includes('müsaitlik sorgusu yapılamamaktadır') || msg.includes('kapalı')) {
        setAvailabilityError(msg);
        setAvailableAsistproIds(new Set());
        setAvailableRoomsData([]);
        setAvailabilityChecked(true);
        setFormData(prev => ({ ...prev, roomTypeId: '' }));
        setPricing({ roomPrice: 0, breakfastPrice: 0, dinnerPrice: 0, totalPrice: 0, nights: 0, billableBreakfastNights: 0, billableDinnerNights: 0 });
      } else {
        console.error('Müsaitlik kontrolü hatası:', err);
        // Hata olursa availability kontrolünü devre dışı bırak (admin devam edebilsin)
        setAvailabilityChecked(false);
      }
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Yemek fiyatlarını ve kapalı günleri backend'den yükle
  const loadMealPrices = async () => {
    try {
      const [dinnerRes, breakfastRes, dinnerSettingsRes] = await Promise.all([
        productsAPI.getByType('dinner').catch(() => null),
        productsAPI.getPublicSetting('lodging_breakfast').catch(() => null),
        productsAPI.getPublicSetting('dinner_public').catch(() => null)
      ]);

      const newPrices = { ...mealPrices };
      
      if (dinnerRes?.data?.data?.content?.price_rules) {
        const dr = dinnerRes.data.data.content.price_rules;
        newPrices.dinner = {
          adult: dr.adult ?? 0,
          child_0_6: dr.child_0_6 ?? 0,
          child_7_12: dr.child_7_12 ?? 0
        };
      }

      if (breakfastRes?.data?.data?.value?.price_rules) {
        const br = breakfastRes.data.data.value.price_rules;
        newPrices.breakfast = {
          adult: br.adult ?? 0,
          child_0_6: br.child_0_6 ?? 0,
          child_7_12: br.child_7_12 ?? 0
        };
      }

      setMealPrices(newPrices);

      // Kapalı günleri backend ayarından yükle
      const dinnerPublicValue = dinnerSettingsRes?.data?.data?.value;
      if (dinnerPublicValue) {
        const normalizedDays: number[] = Array.isArray(dinnerPublicValue.closed_days)
          ? Array.from(new Set<number>(dinnerPublicValue.closed_days
              .map((d: any) => Number(d))
              .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6)))
              .sort((a, b) => a - b)
          : [1];
        setDinnerClosedDays(normalizedDays.length > 0 ? normalizedDays : [1]);
      }
    } catch (e) {
      console.error('Yemek fiyatları yüklenemedi:', e);
    }
  };

  // Load rooms
  useEffect(() => {
    if (isOpen) {
      loadRooms();
      loadMealPrices();
      const defaultCheckIn = addDays(today, 1);
      const defaultCheckOut = addDays(today, 2);
      
      setFormData(prev => ({
        ...prev,
        checkIn: defaultCheckIn,
        checkOut: defaultCheckOut,
        roomTypeId: '',
        adults: 2,
        children: 0,
        childrenAges: [],
        guestName: '',
        guestEmail: '',
        guestPhone: '',
        specialRequests: '',
        includeBreakfast: false,
        includeDinner: false
      }));
      setError('');
      setAvailabilityError('');
      setAvailabilityChecked(false);
      setAvailableAsistproIds(new Set());
      setAvailableRoomsData([]);
      setPricing({ roomPrice: 0, breakfastPrice: 0, dinnerPrice: 0, totalPrice: 0, nights: 0, billableBreakfastNights: 0, billableDinnerNights: 0 });
      setInvoiceType('individual');
      setInvoiceName('');
      setInvoiceTckn('');
      setInvoiceTaxNumber('');
      setInvoiceTaxOffice('');
      setInvoiceAddress('');
      setInvoiceDistrict('');
      setInvoiceCity('');
      setInvoicePostalCode('');
    }
    return () => {
      if (availTimerRef.current) clearTimeout(availTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Çocuk sayısı değişince → yaşları güncelle + eğer seçili oda uygun değilse temizle
  const handleChildrenChange = (newChildren: number) => {
    const newAges = newChildren > 0 ? Array(newChildren).fill(7) : [];
    setFormData(prev => {
      const updated = { ...prev, children: newChildren, childrenAges: newAges };
      // Seçili odanın kapasitesini kontrol et
      if (newChildren > 0 && prev.roomTypeId) {
        const selectedRoom = allRooms.find(r => r.id === prev.roomTypeId);
        if (selectedRoom && selectedRoom.max_children <= 0) {
          updated.roomTypeId = ''; // Uygun olmayan odayı temizle
        }
      }
      return updated;
    });
  };

  // Yetişkin sayısı değişince → eğer seçili oda uygun değilse temizle
  const handleAdultsChange = (newAdults: number) => {
    setFormData(prev => {
      const updated = { ...prev, adults: newAdults };
      if (prev.roomTypeId) {
        const selectedRoom = allRooms.find(r => r.id === prev.roomTypeId);
        if (selectedRoom && newAdults > selectedRoom.max_adults) {
          updated.roomTypeId = '';
        }
      }
      return updated;
    });
  };

  // Fiyat hesaplama — availableRoomsData veya oda/yemek seçimi değişince tetiklenir
  // Kapalı günleri (Pazartesi vb.) dikkate alarak billable gece sayısı hesaplar
  useEffect(() => {
    if (!formData.checkIn || !formData.checkOut || !formData.roomTypeId) return;
    if (formData.checkOut <= formData.checkIn) return;
    if (availableRoomsData.length === 0) return;

    const nights = diffDays(formData.checkIn, formData.checkOut);
    if (nights <= 0) return;

    const selectedRoom = allRooms.find(r => r.id === formData.roomTypeId);
    if (!selectedRoom) return;

    // asistpro_room_id ile eşleştir
    const matchedRoom = availableRoomsData.find((r: any) => r.roomTypeId === selectedRoom.asistpro_room_id)
      || availableRoomsData.find((r: any) => r.roomTypeName === selectedRoom.name);
    const roomPrice = matchedRoom?.price || 0;

    // Yemek fiyatları (backend'den çekilen mealPrices state'inden)
    const bfAdult = mealPrices.breakfast.adult;
    const bfChild7_12 = mealPrices.breakfast.child_7_12;
    const bfChild0_6 = mealPrices.breakfast.child_0_6;
    const dnAdult = mealPrices.dinner.adult;
    const dnChild0_6 = mealPrices.dinner.child_0_6;
    const dnChild7_12 = mealPrices.dinner.child_7_12;

    // Kapalı günleri dikkate alarak billable gece sayısını hesapla
    // Backend mantığıyla aynı: bookings.ts satır 688-702
    let billableBreakfastNights = 0;
    let billableDinnerNights = 0;

    for (let i = 0; i < nights; i++) {
      const [y, m, d] = formData.checkIn.split('-').map(Number);
      const currentDate = new Date(y, m - 1, d);
      currentDate.setDate(currentDate.getDate() + i);
      const dayOfWeek = currentDate.getDay(); // 0=Pazar, 1=Pazartesi, ...

      // Akşam yemeği: O gün kapalı gün mü?
      if (!dinnerClosedDays.includes(dayOfWeek)) {
        billableDinnerNights++;
      }

      // Kahvaltı: Bir sonraki sabah kapalı gün mü? (Pazartesi sabahı = Pazar gecesi kahvaltı yok)
      const nextDay = new Date(currentDate);
      nextDay.setDate(nextDay.getDate() + 1);
      if (!dinnerClosedDays.includes(nextDay.getDay())) {
        billableBreakfastNights++;
      }
    }

    // En az 1 gece olsun (edge case)
    if (billableBreakfastNights === 0) billableBreakfastNights = 1;
    if (billableDinnerNights === 0) billableDinnerNights = 1;

    const getBreakfastPrice = (age: number): number => {
      if (age <= 3) return 0;
      if (age <= 6) return bfChild0_6;
      if (age <= 12) return bfChild7_12;
      return bfAdult;
    };
    const getDinnerPrice = (age: number): number => {
      if (age <= 3) return 0;
      if (age <= 6) return dnChild0_6;
      if (age <= 12) return dnChild7_12;
      return dnAdult;
    };

    const adultBreakfastPerNight = formData.adults * bfAdult;
    const childBreakfastPerNight = formData.childrenAges.reduce((sum, age) => sum + getBreakfastPrice(age), 0);
    const adultDinnerPerNight = formData.adults * dnAdult;
    const childDinnerPerNight = formData.childrenAges.reduce((sum, age) => sum + getDinnerPrice(age), 0);

    // Kapalı günlere denk gelen geceleri çıkararak hesapla
    const breakfastPrice = formData.includeBreakfast ? billableBreakfastNights * (adultBreakfastPerNight + childBreakfastPerNight) : 0;
    const dinnerPrice = formData.includeDinner ? billableDinnerNights * (adultDinnerPerNight + childDinnerPerNight) : 0;
    const totalPrice = roomPrice + breakfastPrice + dinnerPrice;

    setPricing({ roomPrice, breakfastPrice, dinnerPrice, totalPrice, nights, billableBreakfastNights, billableDinnerNights });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRoomsData, formData.roomTypeId, formData.includeBreakfast, formData.includeDinner, formData.adults, formData.children, formData.childrenAges, mealPrices, dinnerClosedDays]);

  const loadRooms = async () => {
    try {
      setLoadingRooms(true);
      const response = await adminAPI.get('/admin/rooms');
      const roomsList = response.data.rooms || response.data.data || [];
      
      const formattedRooms = roomsList.map((room: any) => ({
        id: room.id,
        name: room.room_number || room.room_type_name || 'İsimsiz Oda',
        typeName: room.room_type_name || '',
        max_adults: room.max_adults || 2,
        max_children: room.max_children || 0,
        asistpro_room_id: room.asistpro_room_id || ''
      }));
      
      formattedRooms.sort((a: Room, b: Room) => a.name.localeCompare(b.name, 'tr'));
      
      setAllRooms(formattedRooms);
    } catch (err: any) {
      console.error('Oda listesi yüklenemedi:', err);
      setError('Oda listesi yüklenemedi');
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleCheckInChange = (newCheckIn: string) => {
    let newCheckOut = formData.checkOut;
    if (!newCheckOut || newCheckOut <= newCheckIn) {
      newCheckOut = addDays(newCheckIn, 1);
    }
    setFormData(prev => ({ ...prev, checkIn: newCheckIn, checkOut: newCheckOut }));
    setError('');
  };

  const handleCheckOutChange = (newCheckOut: string) => {
    if (newCheckOut <= formData.checkIn) {
      setError('Çıkış tarihi, giriş tarihinden sonra olmalıdır');
      return;
    }
    setFormData(prev => ({ ...prev, checkOut: newCheckOut }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.guestName || !formData.guestEmail || !formData.guestPhone) {
      setError('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    const phoneValidation = validatePhone(formData.guestPhone);
    if (!phoneValidation.valid) {
      setError(`Telefon numarası hatalı: ${phoneValidation.error}`);
      return;
    }

    if (!formData.roomTypeId) {
      setError('Lütfen bir oda seçin');
      return;
    }

    if (formData.checkOut <= formData.checkIn) {
      setError('Çıkış tarihi, giriş tarihinden sonra olmalıdır');
      return;
    }

    if (pricing.totalPrice <= 0) {
      setError('Toplam tutar 0\'dan büyük olmalıdır');
      return;
    }

    // Fatura validasyonu (rezervasyon yap akışıyla aynı)
    if (!invoiceName.trim()) {
      setError(invoiceType === 'individual' ? 'Fatura için Ad Soyad zorunludur' : 'Firma Ünvanı zorunludur');
      return;
    }
    if (!invoiceAddress.trim()) {
      setError('Fatura adresi zorunludur');
      return;
    }
    if (!invoiceCity.trim()) {
      setError('Fatura için İl zorunludur');
      return;
    }
    if (invoiceType === 'corporate') {
      if (!invoiceTaxNumber.trim()) {
        setError('Vergi numarası zorunludur');
        return;
      }
      if (!invoiceTaxOffice.trim()) {
        setError('Vergi dairesi zorunludur');
        return;
      }
    }

    // Seçili oda bilgisini al (roomTypeName olarak gönder)
    const selectedRoom = allRooms.find(r => r.id === formData.roomTypeId);

    try {
      setLoading(true);

      const bookingData = {
        roomTypeId: selectedRoom?.asistpro_room_id || formData.roomTypeId,
        roomName: selectedRoom?.name || '',
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        adults: formData.adults,
        children: formData.children,
        childrenAges: formData.childrenAges,
        guestName: formData.guestName,
        guestEmail: formData.guestEmail,
        guestPhone: phoneValidation.formatted,
        specialRequests: formData.specialRequests,
        totalPrice: pricing.roomPrice,
        includeBreakfast: formData.includeBreakfast,
        includeDinner: formData.includeDinner,
        breakfastCost: pricing.breakfastPrice,
        dinnerCost: pricing.dinnerPrice,
        invoice_type: invoiceType,
        invoice_name: invoiceName || '',
        ...(invoiceType === 'individual'
          ? {
              invoice_tckn: invoiceTckn || '',
              invoice_address: invoiceAddress || '',
              invoice_district: invoiceDistrict || '',
              invoice_city: invoiceCity || '',
              invoice_postal_code: invoicePostalCode || ''
            }
          : {
              invoice_tax_number: invoiceTaxNumber || '',
              invoice_tax_office: invoiceTaxOffice || '',
              invoice_address: invoiceAddress || '',
              invoice_district: invoiceDistrict || '',
              invoice_city: invoiceCity || '',
              invoice_postal_code: invoicePostalCode || ''
            }
        )
      };

      const response = await adminAPI.post('/admin/bookings/manual-room', bookingData);

      onSuccess(response.data.data);
      
      // Form'u sıfırla
      setFormData({
        roomTypeId: '',
        checkIn: '',
        checkOut: '',
        adults: 2,
        children: 0,
        childrenAges: [],
        guestName: '',
        guestEmail: '',
        guestPhone: '',
        specialRequests: '',
        includeBreakfast: false,
        includeDinner: false
      });
      setInvoiceType('individual');
      setInvoiceName('');
      setInvoiceTckn('');
      setInvoiceTaxNumber('');
      setInvoiceTaxOffice('');
      setInvoiceAddress('');
      setInvoiceDistrict('');
      setInvoiceCity('');
      setInvoicePostalCode('');

    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Rezervasyon oluşturulamadı';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChildAgeChange = (index: number, age: number) => {
    const newAges = [...formData.childrenAges];
    newAges[index] = age;
    setFormData(prev => ({ ...prev, childrenAges: newAges }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content manual-booking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🏨 Manuel Oda Rezervasyonu</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="manual-booking-form">
          {error && (
            <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fee', color: '#c00', borderRadius: '4px' }}>
              ⚠️ {error}
            </div>
          )}

          <div className="form-section">
            <h3>📋 Müşteri Bilgileri</h3>
            
            <div className="form-group">
              <label>Ad Soyad *</label>
              <input
                type="text"
                value={formData.guestName}
                onChange={(e) => setFormData(prev => ({ ...prev, guestName: e.target.value }))}
                placeholder="Ahmet Yılmaz"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>E-posta *</label>
                <input
                  type="email"
                  value={formData.guestEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, guestEmail: e.target.value }))}
                  placeholder="ornek@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Telefon *</label>
                <input
                  type="tel"
                  value={formData.guestPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, guestPhone: e.target.value.replace(/[^0-9+\s]/g, '') }))}
                  onBlur={() => { const v = validatePhone(formData.guestPhone); if (v.valid) setFormData(prev => ({ ...prev, guestPhone: formatPhoneDisplay(formData.guestPhone) })); }}
                  placeholder="+90 553 406 28 44"
                  maxLength={20}
                  required
                />
                {formData.guestPhone && !validatePhone(formData.guestPhone).valid && (
                  <small style={{ color: '#e74c3c', marginTop: '4px', display: 'block' }}>⚠️ {validatePhone(formData.guestPhone).error}</small>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>🏨 Rezervasyon Detayları</h3>
            
            <div className="form-group">
              <label>Oda Seçimi * {loadingAvailability && <span style={{ color: '#999', fontSize: '0.85rem' }}>⏳ Müsaitlik kontrol ediliyor...</span>}</label>
              {availabilityError && (
                <div style={{ background: '#ffeaa7', padding: '8px 12px', borderRadius: '6px', marginBottom: '8px', fontSize: '0.9rem', color: '#d63031' }}>
                  🚫 {availabilityError}
                </div>
              )}
              <select
                value={formData.roomTypeId}
                onChange={(e) => setFormData(prev => ({ ...prev, roomTypeId: e.target.value }))}
                disabled={loadingRooms || loadingAvailability || !!availabilityError}
                required
              >
                <option value="">{loadingRooms ? 'Odalar yükleniyor...' : loadingAvailability ? 'Müsaitlik kontrol ediliyor...' : availabilityError ? 'Tarih kapalı' : 'Oda Seçin'}</option>
                {filteredRooms.map(room => {
                  const asistproId = (room as any).asistpro_room_id;
                  const isAvailable = !availabilityChecked || !asistproId || availableAsistproIds.has(asistproId);
                  return (
                    <option key={room.id} value={room.id} disabled={!isAvailable}>
                      {isAvailable ? '✅' : '🔴'} {room.name} — {room.typeName} (max {room.max_adults}Y+{room.max_children}Ç){!isAvailable ? ' — DOLU' : ''}
                    </option>
                  );
                })}
              </select>
              {formData.children > 0 && allRooms.length > filteredRooms.length && (
                <small style={{ color: '#e67e22', marginTop: '4px', display: 'block' }}>
                  ⚠️ {allRooms.length - filteredRooms.length} oda çocuk kapasitesi nedeniyle gizlendi
                </small>
              )}
              {availabilityChecked && !availabilityError && (
                <small style={{ color: '#27ae60', marginTop: '4px', display: 'block' }}>
                  ✅ {availableAsistproIds.size} oda müsait / {filteredRooms.length - [...filteredRooms].filter(r => availableAsistproIds.has((r as any).asistpro_room_id)).length} oda dolu
                </small>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Giriş Tarihi *</label>
                <input
                  type="date"
                  value={formData.checkIn}
                  min={today}
                  onChange={(e) => handleCheckInChange(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Çıkış Tarihi *</label>
                <input
                  type="date"
                  value={formData.checkOut}
                  min={minCheckOut}
                  onChange={(e) => handleCheckOutChange(e.target.value)}
                  required
                />
                {pricing.nights > 0 && (
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    {pricing.nights} gece konaklama
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Yetişkin Sayısı *</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.adults}
                  onChange={(e) => handleAdultsChange(parseInt(e.target.value) || 1)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Çocuk Sayısı</label>
                <input
                  type="number"
                  min="0"
                  max="5"
                  value={formData.children}
                  onChange={(e) => handleChildrenChange(parseInt(e.target.value) || 0)}
                />
              </div>
            </div>

            {formData.children > 0 && (
              <div className="form-group">
                <label>Çocuk Yaşları</label>
                <div className="children-ages">
                  {formData.childrenAges.map((age, index) => (
                    <select
                      key={index}
                      value={age}
                      onChange={(e) => handleChildAgeChange(index, parseInt(e.target.value))}
                      style={{ width: '80px', marginRight: '0.5rem' }}
                    >
                      {Array.from({ length: 18 }, (_, i) => i).map(a => (
                        <option key={a} value={a}>{a} yaş</option>
                      ))}
                    </select>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Özel İstekler</label>
              <textarea
                value={formData.specialRequests}
                onChange={(e) => setFormData(prev => ({ ...prev, specialRequests: e.target.value }))}
                placeholder="Oda tercihleri, özel istekler vb."
                rows={3}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>🍽️ Ek Hizmetler</h3>
            
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.includeBreakfast}
                  onChange={(e) => setFormData(prev => ({ ...prev, includeBreakfast: e.target.checked }))}
                />
                <span>☕ Kahvaltı Ekle</span>
                {pricing.nights > 0 && formData.includeBreakfast && (
                  <span className="price-hint">
                    {pricing.billableBreakfastNights}/{pricing.nights} gece × ({formData.adults}Y{formData.children > 0 ? `+${formData.children}Ç` : ''}) = ₺{pricing.breakfastPrice.toLocaleString('tr-TR')}
                    {pricing.billableBreakfastNights < pricing.nights && (
                      <span style={{ color: '#e67e22', fontSize: '0.8em' }}> (kapalı gün: {pricing.nights - pricing.billableBreakfastNights} gece düşüldü)</span>
                    )}
                  </span>
                )}
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.includeDinner}
                  onChange={(e) => setFormData(prev => ({ ...prev, includeDinner: e.target.checked }))}
                />
                <span>🍽️ Akşam Yemeği Ekle</span>
                {pricing.nights > 0 && formData.includeDinner && (
                  <span className="price-hint">
                    {pricing.billableDinnerNights}/{pricing.nights} gece × ({formData.adults}Y{formData.children > 0 ? `+${formData.children}Ç` : ''}) = ₺{pricing.dinnerPrice.toLocaleString('tr-TR')}
                    {pricing.billableDinnerNights < pricing.nights && (
                      <span style={{ color: '#e67e22', fontSize: '0.8em' }}> (kapalı gün: {pricing.nights - pricing.billableDinnerNights} gece düşüldü)</span>
                    )}
                  </span>
                )}
              </label>
            </div>
          </div>

          <div className="form-section">
            <h3>🧾 Fatura Bilgileri</h3>

            <div className="form-row" style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="manual_invoice_type"
                  value="individual"
                  checked={invoiceType === 'individual'}
                  onChange={() => setInvoiceType('individual')}
                />
                Bireysel
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="manual_invoice_type"
                  value="corporate"
                  checked={invoiceType === 'corporate'}
                  onChange={() => setInvoiceType('corporate')}
                />
                Kurumsal
              </label>
            </div>

            {invoiceType === 'individual' && (
              <>
                <div className="form-group">
                  <label>Ad Soyad *</label>
                  <input
                    type="text"
                    value={invoiceName}
                    onChange={(e) => setInvoiceName(e.target.value)}
                    placeholder="Ad Soyad"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>T.C. Kimlik No (isteğe bağlı)</label>
                  <input
                    type="text"
                    value={invoiceTckn}
                    onChange={(e) => setInvoiceTckn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="11111111111"
                    maxLength={11}
                  />
                </div>
              </>
            )}

            {invoiceType === 'corporate' && (
              <>
                <div className="form-group">
                  <label>Firma Ünvanı *</label>
                  <input
                    type="text"
                    value={invoiceName}
                    onChange={(e) => setInvoiceName(e.target.value)}
                    placeholder="Firma Ünvanı"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Vergi Numarası *</label>
                    <input
                      type="text"
                      value={invoiceTaxNumber}
                      onChange={(e) => setInvoiceTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="1234567890"
                      maxLength={10}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Vergi Dairesi *</label>
                    <input
                      type="text"
                      value={invoiceTaxOffice}
                      onChange={(e) => setInvoiceTaxOffice(e.target.value)}
                      placeholder="Vergi Dairesi"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Adres *</label>
              <input
                type="text"
                value={invoiceAddress}
                onChange={(e) => setInvoiceAddress(e.target.value)}
                placeholder="Mahalle, Cadde, Sokak, No"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>İlçe</label>
                <input
                  type="text"
                  value={invoiceDistrict}
                  onChange={(e) => setInvoiceDistrict(e.target.value)}
                  placeholder="İlçe"
                />
              </div>
              <div className="form-group">
                <label>İl *</label>
                <input
                  type="text"
                  value={invoiceCity}
                  onChange={(e) => setInvoiceCity(e.target.value)}
                  placeholder="İl"
                  required
                />
              </div>
              <div className="form-group">
                <label>Posta Kodu</label>
                <input
                  type="text"
                  value={invoicePostalCode}
                  onChange={(e) => setInvoicePostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="35220"
                  maxLength={5}
                />
              </div>
            </div>
          </div>

          <div className="pricing-summary">
            {loadingAvailability ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                ⏳ Fiyat hesaplanıyor...
              </div>
            ) : (
              <>
                <div className="price-row">
                  <span>Oda ({pricing.nights} gece):</span>
                  <span>₺{pricing.roomPrice.toLocaleString('tr-TR')}</span>
                </div>
                {pricing.roomPrice === 0 && formData.roomTypeId && pricing.nights > 0 && (
                  <div style={{ color: '#e67e22', fontSize: '0.85rem', margin: '4px 0' }}>
                    ⚠️ Bu oda için fiyat bilgisi alınamadı
                  </div>
                )}
                {pricing.breakfastPrice > 0 && (
                  <div className="price-row">
                    <span>Kahvaltı ({pricing.billableBreakfastNights}/{pricing.nights} gece):</span>
                    <span>₺{pricing.breakfastPrice.toLocaleString('tr-TR')}</span>
                  </div>
                )}
                {pricing.dinnerPrice > 0 && (
                  <div className="price-row">
                    <span>Akşam Yemeği ({pricing.billableDinnerNights}/{pricing.nights} gece):</span>
                    <span>₺{pricing.dinnerPrice.toLocaleString('tr-TR')}</span>
                  </div>
                )}
                <div className="price-row total">
                  <span><strong>TOPLAM:</strong></span>
                  <span><strong>₺{pricing.totalPrice.toLocaleString('tr-TR')}</strong></span>
                </div>
              </>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
              İptal
            </button>
            <button type="submit" className="btn-primary" disabled={loading || loadingAvailability || pricing.totalPrice <= 0}>
              {loading ? 'Oluşturuluyor...' : 'Rezervasyon Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualRoomBookingModal;
